// Bridge `import.meta.env.VITE_NETWORK` → `globalThis.__PARSE_NETWORK__` for @parse/shared's cctp.ts.
//
// WHY: cctp.ts is dual-use — it is also compiled under the backend's CommonJS module setting, so it
// cannot read `import.meta` directly (TS1343 + a parse-time runtime SyntaxError). It instead reads a
// global `__PARSE_NETWORK__` that Vite is supposed to inject via `define` (apps/web/vite.config.ts:30).
//
// THE BUG THIS FIXES: in the Vite DEV server, that `define` replacement does NOT reach the out-of-root
// `packages/shared` module — the served cctp.ts keeps the literal `__PARSE_NETWORK__`, which is
// `undefined` at runtime, so `resolveNetworkEnv()` falls through to 'testnet'. The result is the FE
// silently using TESTNET CCTP contracts (chainId / USDC / tokenMessenger / messageTransmitter) on a
// MAINNET build — while wagmi/feeSplitter (which read `import.meta.env.VITE_NETWORK`) are mainnet. That
// split is what made USDC↔USDC CCTP burns target Base Sepolia (chainId 84532) from a mainnet wallet.
//
// A bare `__PARSE_NETWORK__` reference resolves to `globalThis.__PARSE_NETWORK__` when `define` hasn't
// replaced it, so setting the global here makes cctp.ts resolve the correct network. In a production
// `vite build`, `define` DOES apply, so this assignment is a harmless no-op there (the member-expression
// LHS is not touched by `define`, which only replaces standalone identifiers).
//
// IMPORTANT: this MUST be the first import in main.tsx, before anything that imports cctp.ts, so the
// global is set when cctp.ts first evaluates.
//
// FAIL-LOUD GUARD: resolve through the shared validator. A PRODUCTION bundle with VITE_NETWORK unset —
// or ANY unrecognized value — would otherwise silently use TESTNET contracts + a devnet Solana RPC while
// real wallets connect. Rather than boot a mislabeled UI, refuse and render a visible reason.
import { resolveBuildNetwork } from './network'

let resolvedNetwork: string
try {
  resolvedNetwork = resolveBuildNetwork(import.meta.env.VITE_NETWORK, import.meta.env.PROD)
} catch (err) {
  const msg = err instanceof Error ? err.message : String(err)
  // eslint-disable-next-line no-console
  console.error('[network-init] FATAL network misconfiguration —', msg)
  if (typeof document !== 'undefined') {
    const host = document.getElementById('root') || document.body
    if (host) {
      const box = document.createElement('div')
      box.setAttribute(
        'style',
        'max-width:40rem;margin:20vh auto;padding:1.5rem;border:1px solid #b91c1c;border-radius:8px;' +
          'color:#b91c1c;background:#fef2f2;text-align:center;font-family:system-ui,-apple-system,sans-serif',
      )
      const h = document.createElement('h1')
      h.setAttribute('style', 'font-size:1.1rem;margin:0 0 .5rem')
      h.textContent = 'Network misconfiguration'
      const p = document.createElement('p')
      p.setAttribute('style', 'margin:0;font-size:.9rem')
      p.textContent = msg // textContent (not innerHTML) — never inject the raw value as markup
      box.appendChild(h)
      box.appendChild(p)
      host.replaceChildren(box)
    }
  }
  throw err // stop boot — do NOT let the app render with the wrong network's contracts
}

;(globalThis as unknown as { __PARSE_NETWORK__?: string }).__PARSE_NETWORK__ = resolvedNetwork
